import React from "react";
import { toast } from "sonner";
import { UserPlus, UserCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { requireAuth } from "./SignInGate";

/**
 * SubscribeButton — single primitive used by Reels, ListingDetail, and
 * Digital Store pages to follow another user / store. Talks to
 * /api/users/{user_id}/subscribe (toggle) and reflects the live state
 * locally so the icon swaps without a refetch.
 *
 * Props
 *   userId         (string, required) target seller / store owner id
 *   initialSubscribed (bool) seeded state from the feed payload
 *   initialCount   (number, optional) seeded subscriber count
 *   isSelf         (bool, optional) hides the button if true
 *   variant        "default" | "compact" | "pill-light" | "reels"
 *                  picks the visual treatment to match each surface
 *   showCount      (bool) renders "1.2k subscribers" next to the icon
 *   testId         (string, optional) override data-testid prefix
 *
 * Behavior
 *   • Unauth users tap → toast + redirect to /login?next={current}.
 *   • Self → button not rendered (server enforces too).
 *   • Optimistic toggle, rolls back + toasts on network error.
 */
function _fmt(n) {
    if (n == null) return "0";
    if (n < 1000) return String(n);
    if (n < 10000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
    return Math.round(n / 1000) + "k";
}

export default function SubscribeButton({
    userId,
    initialSubscribed = false,
    initialCount,
    isSelf = false,
    variant = "default",
    showCount = false,
    testId,
}) {
    const { user } = useAuth();
    const [sub, setSub] = React.useState(!!initialSubscribed);
    const [count, setCount] = React.useState(typeof initialCount === "number" ? initialCount : null);
    const [busy, setBusy] = React.useState(false);

    // If parent re-renders with a fresh seed (e.g. switching reels slide),
    // sync local state. We DON'T sync on every prop change — only when the
    // identity of the target changes — so an optimistic flip doesn't get
    // clobbered by a stale prop.
    const lastSeed = React.useRef({ userId, initialSubscribed });
    React.useEffect(() => {
        if (lastSeed.current.userId !== userId) {
            lastSeed.current = { userId, initialSubscribed };
            setSub(!!initialSubscribed);
            setCount(typeof initialCount === "number" ? initialCount : null);
        }
    }, [userId, initialSubscribed, initialCount]);

    // Hydrate the live count when the parent didn't pass one in. Single
    // GET — server is fast and the response is small.
    React.useEffect(() => {
        if (count !== null || !userId) return;
        let alive = true;
        api.get(`/users/${userId}/subscribe`).then((r) => {
            if (!alive || !r?.data) return;
            setSub(!!r.data.subscribed);
            setCount(Number(r.data.subscriber_count || 0));
        }).catch(() => {});
        return () => { alive = false; };
    }, [userId, count]);

    if (isSelf) return null;

    const toggle = async (ev) => {
        ev?.preventDefault?.();
        ev?.stopPropagation?.();
        if (busy) return;
        if (!user) {
            // v78.59 — inline modal instead of redirecting away.
            const ok = await requireAuth("Sign in to subscribe to this seller");
            if (!ok) return;
        }
        // Optimistic flip.
        const prevSub = sub, prevCount = count ?? 0;
        const nextSub = !prevSub;
        setSub(nextSub);
        setCount(prevCount + (nextSub ? 1 : -1));
        setBusy(true);
        try {
            const r = await api.post(`/users/${userId}/subscribe`);
            setSub(!!r?.data?.subscribed);
            setCount(Number(r?.data?.subscriber_count || 0));
            toast.success(r?.data?.subscribed ? "Subscribed" : "Unsubscribed", {
                description: r?.data?.subscribed
                    ? "We'll notify you in your Alerts when this seller posts new ads."
                    : "You won't see new-ad alerts from this seller anymore.",
            });
        } catch (e) {
            // Rollback.
            setSub(prevSub);
            setCount(prevCount);
            toast.error("Couldn't update subscription", { description: formatApiError(e) });
        } finally {
            setBusy(false);
        }
    };

    const Icon = sub ? UserCheck : UserPlus;
    const label = sub ? "Subscribed" : "Subscribe";

    const variants = {
        default:
            `inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full font-bold text-xs sm:text-sm transition-all ${
                sub
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100"
                    : "bg-aduku-navy text-aduku-yellow hover:bg-aduku-navy-700"
            }`,
        compact:
            `inline-flex items-center gap-1 h-7 px-2.5 rounded-full font-bold text-[11px] transition-all ${
                sub
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-300"
                    : "bg-aduku-navy text-aduku-yellow hover:bg-aduku-navy-700"
            }`,
        "pill-light":
            `inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full font-bold text-xs sm:text-sm transition-all border ${
                sub
                    ? "bg-white/10 text-white border-white/30"
                    : "bg-aduku-yellow text-aduku-navy border-aduku-yellow hover:bg-aduku-yellow/90"
            }`,
        reels:
            // Stacked icon-with-label tile that fits the Reels right rail.
            // Renders the count BELOW the icon to mimic Reels apps.
            `flex flex-col items-center justify-center gap-1 w-12 transition-transform active:scale-90 ${
                sub ? "text-emerald-300" : "text-white"
            }`,
    };

    if (variant === "reels") {
        return (
            <button
                type="button"
                data-testid={testId || "subscribe-btn-reels"}
                onClick={toggle}
                disabled={busy}
                aria-pressed={sub}
                className={variants.reels}
            >
                <span className={`w-11 h-11 rounded-full flex items-center justify-center ${sub ? "bg-emerald-500/25 ring-2 ring-emerald-300" : "bg-white/15 backdrop-blur"}`}>
                    <Icon className="w-5 h-5" strokeWidth={2.4} />
                </span>
                <span className="text-[10px] font-bold leading-none">{sub ? "Subscribed" : "Subscribe"}</span>
                {showCount && count !== null && (
                    <span className="text-[10px] text-white/75 leading-none">{_fmt(count)}</span>
                )}
            </button>
        );
    }

    return (
        <button
            type="button"
            data-testid={testId || "subscribe-btn"}
            onClick={toggle}
            disabled={busy}
            aria-pressed={sub}
            className={variants[variant] || variants.default}
        >
            <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
            <span>{label}</span>
            {showCount && count !== null && (
                <span className={sub ? "text-emerald-700/70" : "text-aduku-yellow/80"}>· {_fmt(count)}</span>
            )}
        </button>
    );
}
